import { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { X, CloudArrowUp, FileXls, Trash, CheckCircle, Warning } from "@phosphor-icons/react";

/**
 * Upload a RepairDesk "Multi-Store Summary" .xlsx and import the totals as
 * a historical-revenue baseline for ONE selected store. The admin picks the
 * target store first, then uploads the file — every numeric total in the
 * file is summed and assigned to that store. This avoids ambiguity when the
 * file contains several rows (breakdown sub-rows, other stores, TOTALS) and
 * gives the operator explicit control over which store in the POS receives
 * the baseline.
 *
 * Expected columns (RepairDesk v1 multi-store-summary):
 *   Store Name | Repairs | Products | Unlocking | Trade-In | Miscellaneous |
 *   Total Sales | Payments Received | Discounts | COGS | Profit After Tax |
 *   Profit Margin % | Avg Sales | Tax
 * Only `Store Name`, `Total Sales`, `Tax`, `Discounts` and category subtotals
 * are used — the rest are ignored.
 */
export default function ImportGrossModal({ open, onClose, onImported }) {
  const [stores, setStores] = useState([]);
  const [targetStoreId, setTargetStoreId] = useState("");
  const [fileName, setFileName] = useState("");
  const [parsed, setParsed] = useState(null);   // {rows:[{label, gross, tax, discounts}], totals:{gross, tax, discounts, count}}
  const [existing, setExisting] = useState([]);
  const [period, setPeriod] = useState(() => {
    // Default: last 2 years up to yesterday — a sensible guess the admin can tweak.
    const end = new Date();
    end.setDate(end.getDate() - 1);
    const start = new Date();
    start.setFullYear(start.getFullYear() - 2);
    return {
      start: start.toISOString().slice(0, 10),
      end: end.toISOString().slice(0, 10),
    };
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/stores").then((r) => setStores(r.data || [])).catch(() => {});
    api.get("/integrations/historical-summary").then((r) => setExisting(r.data?.summaries || [])).catch(() => {});
    setTargetStoreId("");
    setParsed(null);
    setFileName("");
  }, [open]);

  // --- XLSX parse: sum every numeric row in the sheet --------------------
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    try {
      const buf = await f.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      // Prefer the sheet named "Store Summary" but fall back to the first.
      const sheetName = wb.SheetNames.find((n) => /store.*summary/i.test(n)) || wb.SheetNames[0];
      const ws = wb.Sheets[sheetName];
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null });
      if (!rows.length) {
        toast.error("The file has no rows");
        return;
      }
      // Row 0 is the header; find column indices from it.
      const header = (rows[0] || []).map((h) => (h == null ? "" : String(h).trim().toLowerCase()));
      const col = (key) => header.findIndex((h) => h === key.toLowerCase());
      const idx = {
        name: col("Store Name"),
        repairs: col("Repairs"),
        products: col("Products"),
        unlocking: col("Unlocking"),
        tradein: col("Trade-In"),
        misc: col("Miscellaneous"),
        total: col("Total Sales"),
        discounts: col("Discounts"),
        tax: col("Tax"),
      };
      if (idx.name < 0 || idx.total < 0) {
        toast.error("Missing required columns: 'Store Name' and 'Total Sales'");
        return;
      }
      // Collect only STORE rows — skip breakdown sub-rows and TOTALS. A "store
      // row" is one with a non-empty name that isn't a reserved label and has
      // a Total Sales value. If the file has multiple store rows, we take the
      // HIGHEST Total Sales (typically the "real" row; the TOTALS row is
      // excluded by label, and breakdown sub-rows have tiny values).
      const reserved = /^(totals?|sales|number of transactions|average transaction size)$/i;
      const storeRows = [];
      for (let i = 1; i < rows.length; i++) {
        const r = rows[i] || [];
        const label = String(r[idx.name] || "").trim();
        if (!label || reserved.test(label)) continue;
        const gross = Number(r[idx.total] || 0);
        if (!gross) continue;
        storeRows.push({
          label,
          gross,
          tax: idx.tax >= 0 ? Number(r[idx.tax] || 0) : 0,
          discounts: idx.discounts >= 0 ? Number(r[idx.discounts] || 0) : 0,
          categories: {
            repairs: idx.repairs >= 0 ? Number(r[idx.repairs] || 0) : 0,
            products: idx.products >= 0 ? Number(r[idx.products] || 0) : 0,
            unlocking: idx.unlocking >= 0 ? Number(r[idx.unlocking] || 0) : 0,
            trade_in: idx.tradein >= 0 ? Number(r[idx.tradein] || 0) : 0,
            miscellaneous: idx.misc >= 0 ? Number(r[idx.misc] || 0) : 0,
          },
        });
      }
      if (!storeRows.length) {
        toast.error("No store rows with revenue found in the file");
        return;
      }
      // Sum across every store row found (so if the file already contains
      // multiple stores, they're collapsed into one baseline for the target
      // store the user picks).
      const totals = storeRows.reduce(
        (acc, r) => ({
          gross: acc.gross + r.gross,
          tax: acc.tax + r.tax,
          discounts: acc.discounts + r.discounts,
          categories: {
            repairs: acc.categories.repairs + r.categories.repairs,
            products: acc.categories.products + r.categories.products,
            unlocking: acc.categories.unlocking + r.categories.unlocking,
            trade_in: acc.categories.trade_in + r.categories.trade_in,
            miscellaneous: acc.categories.miscellaneous + r.categories.miscellaneous,
          },
        }),
        { gross: 0, tax: 0, discounts: 0, categories: { repairs: 0, products: 0, unlocking: 0, trade_in: 0, miscellaneous: 0 } }
      );
      setParsed({ rows: storeRows, totals });
      toast.success(`Parsed ${storeRows.length} row${storeRows.length === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error("Could not parse file: " + err.message);
    }
  };

  const submit = async () => {
    if (!targetStoreId) return toast.error("Pick a target store first");
    if (!parsed) return toast.error("Upload the XLSX first");
    if (!period.start || !period.end || period.start > period.end) {
      return toast.error("Pick a valid period (start must be on or before end)");
    }
    setBusy(true);
    try {
      const storeName = stores.find((s) => s.id === targetStoreId)?.name || "";
      const payload = {
        source: "repairdesk",
        replace_existing: true,
        rows: [{
          store_id: targetStoreId,
          label: storeName,
          period_start: period.start,
          period_end: period.end,
          total_sales: parsed.totals.gross,
          tax: parsed.totals.tax,
          discounts: parsed.totals.discounts,
          transaction_count: 0,
          category_breakdown: parsed.totals.categories,
        }],
      };
      const { data } = await api.post("/integrations/historical-summary/import", payload);
      toast.success(`Imported baseline for ${storeName}`);
      onImported?.();
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const removeExisting = async (id) => {
    if (!window.confirm("Remove this imported baseline?")) return;
    try {
      await api.delete(`/integrations/historical-summary/${id}`);
      setExisting((prev) => prev.filter((x) => x.id !== id));
      toast.success("Removed");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const storeLookup = useMemo(() => new Map((stores || []).map((s) => [s.id, s.name])), [stores]);
  const targetStoreName = stores.find((s) => s.id === targetStoreId)?.name || "";

  // Warn the admin if there's already a baseline for this store — confirming
  // the "replace_existing" behaviour so they don't accidentally double-count.
  const existingForTarget = existing.filter((h) => h.store_id === targetStoreId);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-[#E5E7EB] rounded-2xl" onClick={(e) => e.stopPropagation()} data-testid="import-gross-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E7EB] sticky top-0 bg-white z-10">
          <div>
            <div className="overline mb-1">REPAIRDESK GROSS IMPORT</div>
            <h2 className="font-display font-semibold text-[20px] tracking-tight">Historical sales baseline</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#F8FAFC] rounded-full" data-testid="import-gross-close"><X size={18} weight="bold" /></button>
        </div>

        <div className="p-6 space-y-5">
          {/* Existing imports list */}
          {existing.length > 0 && (
            <section className="rounded-xl border border-[#E5E7EB] bg-[#F8FAFC] p-4" data-testid="existing-summaries">
              <div className="overline mb-2">CURRENTLY STORED ({existing.length})</div>
              <div className="space-y-2">
                {existing.map((h) => (
                  <div key={h.id} className="flex items-center gap-3 text-[12px]">
                    <CheckCircle size={14} weight="bold" className="text-[#16A34A] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-[#121216] truncate">{storeLookup.get(h.store_id) || h.label || "—"}</div>
                      <div className="text-[11px] text-[#9CA3AF] font-mono">
                        {h.period_start} → {h.period_end} · ${Number(h.total_sales || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} · {h.source}
                      </div>
                    </div>
                    <button onClick={() => removeExisting(h.id)} className="p-1.5 hover:bg-[#FEE2E2] rounded-lg text-[#B91C1C]" title="Remove">
                      <Trash size={13} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Step 1 — pick the target store FIRST */}
          <section className="rounded-xl border border-[#E5E7EB] p-4" data-testid="target-store-section">
            <div className="overline mb-2">1. TARGET STORE</div>
            <p className="text-[11px] text-[#4B5563] mb-2 leading-relaxed">
              Pick which store this historical data belongs to. Everything in the file will be assigned to this store only.
            </p>
            <select
              value={targetStoreId}
              onChange={(e) => setTargetStoreId(e.target.value)}
              className={`w-full h-11 px-3 text-[13px] rounded-lg border focus:outline-none focus:border-[#1E5FEB] ${targetStoreId ? "border-[#E5E7EB]" : "border-[#FCA5A5] bg-[#FEF2F2]"}`}
              data-testid="target-store-select"
            >
              <option value="">— select a store —</option>
              {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            {existingForTarget.length > 0 && (
              <div className="mt-3 flex items-start gap-2 text-[11px] text-[#B45309] bg-[#FEF3C7] border border-[#FDE68A] rounded-lg px-3 py-2" data-testid="replace-warning">
                <Warning size={13} weight="bold" className="shrink-0 mt-0.5" />
                <div>
                  <span className="font-semibold">Existing baseline will be replaced.</span>{" "}
                  {targetStoreName} already has ${Number(existingForTarget[0].total_sales || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} stored for {existingForTarget[0].period_start} → {existingForTarget[0].period_end}. It will be overwritten on import.
                </div>
              </div>
            )}
          </section>

          {/* Step 2 — period the data covers */}
          <section className="rounded-xl border border-[#E5E7EB] p-4" data-testid="period-section">
            <div className="overline mb-2">2. PERIOD THIS DATA COVERS</div>
            <p className="text-[11px] text-[#4B5563] mb-2 leading-relaxed">
              These figures will count in any Reports view whose date range <span className="font-semibold">overlaps</span> this period.
            </p>
            <div className="flex items-end gap-3 flex-wrap">
              <label className="block">
                <div className="text-[11px] uppercase tracking-[0.15em] text-[#9CA3AF] font-semibold mb-1">From</div>
                <input type="date" value={period.start} onChange={(e) => setPeriod((p) => ({ ...p, start: e.target.value }))}
                       className="h-10 px-3 text-[12px] rounded-lg border border-[#E5E7EB] focus:outline-none focus:border-[#1E5FEB]" data-testid="period-start" />
              </label>
              <label className="block">
                <div className="text-[11px] uppercase tracking-[0.15em] text-[#9CA3AF] font-semibold mb-1">To</div>
                <input type="date" value={period.end} onChange={(e) => setPeriod((p) => ({ ...p, end: e.target.value }))}
                       className="h-10 px-3 text-[12px] rounded-lg border border-[#E5E7EB] focus:outline-none focus:border-[#1E5FEB]" data-testid="period-end" />
              </label>
            </div>
          </section>

          {/* Step 3 — upload the XLSX */}
          <section className="rounded-xl border border-[#E5E7EB] p-4" data-testid="upload-section">
            <div className="overline mb-2">3. UPLOAD REPAIRDESK XLSX</div>
            {parsed ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-[12px] text-[#4B5563]">
                  <FileXls size={15} weight="bold" className="text-[#1E5FEB]" />
                  <span className="font-mono">{fileName}</span>
                  <button
                    onClick={() => { setParsed(null); setFileName(""); }}
                    className="ml-auto text-[11px] text-[#1E5FEB] hover:underline"
                    data-testid="replace-file"
                  >
                    Replace file
                  </button>
                </div>
                {/* Summary card */}
                <div className="rounded-lg border border-[#E5E7EB] bg-[#F8FAFC] p-4" data-testid="parsed-summary">
                  <div className="text-[11px] uppercase tracking-[0.14em] text-[#4B5563] font-semibold mb-3">
                    Going to {targetStoreName || "— pick a store —"}
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <StatCell label="Gross" value={`$${parsed.totals.gross.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} big />
                    <StatCell label="Tax" value={`$${parsed.totals.tax.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
                    <StatCell label="Discounts" value={`$${parsed.totals.discounts.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
                  </div>
                  {parsed.rows.length > 1 && (
                    <div className="mt-3 pt-3 border-t border-[#E5E7EB]">
                      <div className="text-[10px] uppercase tracking-[0.14em] text-[#4B5563] font-semibold mb-2">
                        Summed from {parsed.rows.length} row{parsed.rows.length === 1 ? "" : "s"} in the file:
                      </div>
                      <div className="space-y-1">
                        {parsed.rows.map((r, i) => (
                          <div key={i} className="flex items-center justify-between text-[11px]" data-testid={`row-preview-${i}`}>
                            <span className="text-[#4B5563]">{r.label}</span>
                            <span className="font-mono text-[#121216]">${r.gross.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <label className="block rounded-lg border-2 border-dashed border-[#E5E7EB] bg-[#F8FAFC] hover:border-[#1E5FEB] hover:bg-blue-50/30 cursor-pointer transition-colors p-6 text-center">
                <CloudArrowUp size={30} weight="duotone" className="mx-auto text-[#1E5FEB] mb-1.5" />
                <div className="font-semibold text-[13px] text-[#121216]">Choose the RepairDesk XLSX</div>
                <div className="text-[11px] text-[#4B5563] mt-1">Multi-Store Summary export — sheet "Store Summary"</div>
                <input type="file" accept=".xlsx,.xls" className="hidden" onChange={onFile} data-testid="import-gross-file" />
              </label>
            )}
          </section>
        </div>

        <div className="flex border-t border-[#E5E7EB] bg-white sticky bottom-0">
          <button type="button" onClick={onClose} className="flex-1 h-12 text-[12px] font-semibold" data-testid="import-gross-cancel">CANCEL</button>
          <button
            onClick={submit}
            disabled={busy || !parsed || !targetStoreId}
            className="flex-1 h-12 bg-[#1E5FEB] hover:bg-[#1849C8] disabled:bg-zinc-300 text-white text-[12px] font-semibold flex items-center justify-center gap-2"
            data-testid="import-gross-submit"
          >
            {busy ? "IMPORTING…" : targetStoreName ? `IMPORT TO ${targetStoreName.toUpperCase()}` : "IMPORT"}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatCell({ label, value, big }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.14em] text-[#9CA3AF] font-semibold">{label}</div>
      <div className={`font-mono ${big ? "text-[18px] font-bold text-[#121216]" : "text-[13px] font-semibold text-[#4B5563]"}`}>{value}</div>
    </div>
  );
}
